import { Question, QuestionSet, SessionStage } from '@/types';
import { authService } from '@/services/auth';
import logger from '@/utils/logger';
import { transformQuestionSetToGameData } from '@/utils/questionSetUtils';
import { LOCAL_AI_QUESTION_SET } from '@/config/localAiQuestionSet';

export const AI_QUESTION_SET_ID = '56fNn5YhbdoBAydCQ9do';
export const AI_QUESTION_SET_NAME = 'AI_Question_Sample';

export interface AiGameContent {
  questionSetId: string;
  questionSetName: string;
  questions: Question[];
  stages: SessionStage[];
}

function shouldUseFallback(error: unknown): boolean {
  if (!error) return false;

  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';

  if (typeof message !== 'string') {
    return false;
  }

  const lowered = message.toLowerCase();
  return (
    lowered.includes('missing or insufficient permissions') ||
    lowered.includes('permission-denied') ||
    lowered.includes('not-found')
  );
}

function getFallbackQuestionSet(reason?: unknown): QuestionSet {
  if (reason) {
    logger.warn('Using local fallback AI question set due to Firestore access issue.', reason);
  } else {
    logger.warn('Using local fallback AI question set.');
  }
  return LOCAL_AI_QUESTION_SET;
}

export async function loadAiGameContent(): Promise<AiGameContent> {
  try {
    logger.network(`Loading AI question set (${AI_QUESTION_SET_ID})`);

    let questionSet: QuestionSet | null = null;

    try {
      questionSet = await authService.getQuestionSet(AI_QUESTION_SET_ID);
    } catch (error) {
      if (shouldUseFallback(error)) {
        questionSet = getFallbackQuestionSet(error);
      } else {
        logger.error('Failed to load AI question content', error);
        throw error instanceof Error ? error : new Error(String(error));
      }
    }

    if (!questionSet) {
      logger.warn('AI question set not found in Firestore. Falling back to local sample.');
      questionSet = getFallbackQuestionSet();
    }

    if (!questionSet) {
      throw new Error('AI question set not found');
    }

    if (questionSet.name !== AI_QUESTION_SET_NAME) {
      logger.warn(
        `AI question set name mismatch. Expected "${AI_QUESTION_SET_NAME}", received "${questionSet.name}".`
      );
    }

    const { questions, stages } = transformQuestionSetToGameData(questionSet);

    if (!questions.length) {
      throw new Error('AI question set contains no questions');
    }

    logger.success(`Loaded AI question set "${questionSet.name}" with ${questions.length} questions.`);

    return {
      questionSetId: questionSet.id,
      questionSetName: questionSet.name ?? AI_QUESTION_SET_NAME,
      questions,
      stages
    };
  } catch (error) {
    logger.error('Failed to load AI question content', error);
    throw error instanceof Error
      ? error
      : new Error('Failed to load AI question content');
  }
}

export function validateQuestionCount(questions: Question[], expectedCount?: number): boolean {
  if (typeof expectedCount === 'number') {
    const isValid = questions.length === expectedCount;
    if (!isValid) {
      logger.error(
        `Invalid question count: expected ${expectedCount}, received ${questions.length}`
      );
    }
    return isValid;
  }

  const hasQuestions = questions.length > 0;

  if (!hasQuestions) {
    logger.error('Invalid question count: at least one question is required.');
  }

  return hasQuestions;
}
